import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Link } from '@components/atoms/link';
import { Typography } from '@components/atoms/typography';
import { ExpandIcon } from '@components/icons';
import { Checkbox } from '@components/moleculs/checkbox';
import { useExpiredTimer } from '@components/organisms/verification-preview-container-default/organisms/general-info/hooks/use-expired-timer';
import { usePolicyLinks } from '@hooks/domain';
import { DocsVerificationStepEnum } from '@models/enums';
import { Header } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/header';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { useAppTheme } from '@theme';
import { isDesktop } from '@utils/util';

import { FaqAnswer } from './faq-answer';
import { RestrictedCountriesModal } from './restricted-countries-modal';
import { getStartCustomPartnerSx } from './styles';

const FAQ_ITEMS = [
    'whatIsDataSpike',
    'howDoesItWork',
    'dataProtection',
    'howLongDoesItTake',
] as const;

export const StartCustomPartner: React.FC = () => {
    const { scrollTop } = useWidgetContext();
    const { t } = useTranslation();
    const theme = useAppTheme();
    const sx = useMemo(() => getStartCustomPartnerSx(theme), [theme]);
    const dispatch = useAppDispatch();
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const isTermsAccepted = useAppSelector((state) => state.docsVerification.isTermsAccepted);
    const { termsLink, privacyLink } = usePolicyLinks();

    const [isTermsConfirmation, setIsTermsConfirmation] = useState(() => isTermsAccepted);
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const [isRestrictedCountriesModalOpen, setIsRestrictedCountriesModalOpen] = useState(false);
    const { timeToExpire, isExpired } = useExpiredTimer(sdkOptions?.expiresAt ?? '');

    const startVerificationDisabled = !isTermsConfirmation;

    useEffect(() => {
        if (isExpired) {
            dispatch(
                docsVerificationSlice.actions.setCurrentStep(DocsVerificationStepEnum.EXPIRED),
            );
        }
    }, [isExpired, dispatch]);

    const handleStartVerificationClick = async () => {
        dispatch(docsVerificationSlice.actions.setIsTermsAccepted(true));
        if (!sdkOptions?.settings?.allow_desktop && isDesktop()) {
            dispatch(
                docsVerificationSlice.actions.setCurrentStep(
                    DocsVerificationStepEnum.DESKTOP_NOT_ALLOWED,
                ),
            );
        } else {
            dispatch(docsVerificationSlice.actions.setCurrentStep(DocsVerificationStepEnum.START));
        }
        setTimeout(() => {
            scrollTop();
        });
    };

    const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsTermsConfirmation(e.target.checked);
    };

    const faqItems = useMemo(
        () =>
            FAQ_ITEMS.map((key) => ({
                key,
                question: t(`docVerificationWidget.startCustomPartner.faq.${key}.question`),
            })),
        [t],
    );

    const handleRestrictedCountriesClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setIsRestrictedCountriesModalOpen(true);
    };

    if (isCameraNotAllowed) {
        return (
            <CameraAccessDefault
                onClose={() => {
                    setIsCameraNotAllowed(false);
                }}
            />
        );
    }

    return (
        <Stack sx={sx.container}>
            <Header />
            <Box sx={sx.divider} />
            <Stack sx={sx.titleSection}>
                <Typography tag='h2' variant='h2' color='text-main'>
                    {t('docVerificationWidget.startCustomPartner.titleLine1')}
                </Typography>
                {sdkOptions?.expiresAt && (
                    <Typography tag='p' variant='text-bold' color='primary-a1'>
                        {t('docVerificationWidget.startCustomPartner.sessionExpires', {
                            time: timeToExpire,
                        })}
                    </Typography>
                )}
            </Stack>

            <Stack sx={sx.contentCard}>
                <Stack sx={sx.faqList}>
                    {faqItems.map(({ key, question }) => (
                        <Accordion
                            key={key}
                            disableGutters={true}
                            elevation={0}
                            square={true}
                            sx={sx.faqAccordion}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandIcon />}
                                sx={sx.faqAccordionSummary}
                            >
                                {question}
                            </AccordionSummary>
                            <AccordionDetails sx={sx.faqAccordionDetails}>
                                <FaqAnswer faqKey={key} />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Stack>

                <Stack sx={{ ...sx.noticeBox, mb: 0 }}>
                    <Typography tag='p' variant='big-text-bold' color='text-main'>
                        {t('docVerificationWidget.startCustomPartner.notice.title')}
                    </Typography>
                    <Typography tag='p' variant='text' sx={sx.noticeText}>
                        <Trans
                            i18nKey='docVerificationWidget.startCustomPartner.notice.text'
                            components={{
                                link1: (
                                    <Link
                                        href='#'
                                        rel='noreferrer'
                                        onClick={handleRestrictedCountriesClick}
                                    />
                                ),
                            }}
                        />
                    </Typography>
                </Stack>

                <Stack sx={sx.footerSection}>
                    <Box sx={sx.termsCheckbox}>
                        <Checkbox
                            checked={isTermsConfirmation}
                            onChange={handleTermsChange}
                            sx={{
                                ml: -0.25,
                                mt: 1.5,
                                '& > span': {
                                    lineHeight: '16px !important',
                                },
                            }}
                            label={
                                <Typography variant='small-text'>
                                    <Trans
                                        i18nKey='docVerificationWidget.startCustomPartner.terms'
                                        components={{
                                            link1: (
                                                <Link
                                                    href={termsLink}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                />
                                            ),
                                            link2: (
                                                <Link
                                                    href={privacyLink}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                />
                                            ),
                                        }}
                                    />
                                </Typography>
                            }
                        />
                    </Box>
                    <Button
                        variant='primary'
                        onClick={handleStartVerificationClick}
                        disabled={startVerificationDisabled}
                        size='xl'
                        sx={sx.startButton}
                    >
                        {t('common.next')}
                    </Button>
                    <Typography
                        tag='p'
                        variant='small-text-bold'
                        color='text-light'
                        sx={sx.providerText}
                    >
                        {t('docVerificationWidget.startCustomPartner.providerText')}
                    </Typography>
                </Stack>
            </Stack>

            <RestrictedCountriesModal
                open={isRestrictedCountriesModalOpen}
                onClose={() => {
                    setIsRestrictedCountriesModalOpen(false);
                }}
            />
        </Stack>
    );
};
